import {
  Button,
  Col,
  Form,
  InputNumber,
  Modal,
  Row,
  Select,
  Typography,
} from "antd";
import {
  COST_HEAD_LIST_ENDPOINT,
  COST_TYPE_LIST_ENDPOINT,
  EXIM_CHARGES_ADD_ENDPOINT,
  EXIM_CHARGES_DELETE_ENDPOINT,
  EXIM_CHARGES_EDIT_ENDPOINT,
  EXIM_CHARGES_LIST_ENDPOINT,
} from "apiServices/API_ENDPOINTS";
import { deleteData, getData, postData, putData } from "apiServices/common";
import AppContent from "common/AppContent";
import ListTable from "common/ListTable";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { NO_PERMISSION_REQUIRED } from "routes/permissions";
import { hasPermission } from "utils/functions";

export default function Charges() {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [costHeadOptions, setCostHeadOptions] = useState([]);
  const [costTypeOptions, setCostTypeOptions] = useState([]);

  // Antd
  const [chargesForm] = Form.useForm();
  const { Title } = Typography;

  // Redux
  const filterData = useSelector(({ filter }) => filter.filterData);

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      localStorage.setItem(
        "filter_status",
        JSON.stringify({
          ...parsedFilterStatus,
          [routerPath]: true,
        }),
      );
    } else {
      localStorage.setItem(
        "filter_status",
        JSON.stringify({
          ...parsedFilterStatus,
          [routerPath]: false,
        }),
      );
    }
  }, [showFilter, routerPath]);

  // Fetch Cost Head options
  const fetchCostHeadOptions = useCallback(async () => {
    try {
      const response = await getData(`${COST_HEAD_LIST_ENDPOINT}`, null, {
        per_page: 1000,
      });
      if (response?.status === 200) {
        const data = response.data?.data?.data || [];
        const options = data.map((item) => ({
          value: item.id,
          label: item.name,
        }));
        setCostHeadOptions(options);
      }
    } catch (error) {
      console.error("Error fetching cost head options:", error);
    }
  }, []);

  // Fetch Cost Type options
  const fetchCostTypeOptions = useCallback(async () => {
    try {
      const response = await getData(COST_TYPE_LIST_ENDPOINT, null, {
        per_page: 1000,
      });
      if (response?.status === 200) {
        const data = response.data?.data?.data || [];
        const options = data.map((item) => ({
          value: item.id,
          label: item.name,
        }));
        setCostTypeOptions(options);
      }
    } catch (error) {
      console.error("Error fetching cost type options:", error);
    }
  }, []);

  // Load dropdown options when modal opens
  useEffect(() => {
    if (isModalOpen) {
      fetchCostHeadOptions();
      fetchCostTypeOptions();
    }
  }, [isModalOpen, fetchCostHeadOptions, fetchCostTypeOptions]);

  const getAllCharges = useCallback(
    async (filterValues) => {
      const { cost_head_id, cost_type_id } = filterValues || {};

      setLoading(true);
      setFetchError(null);

      const query = `${EXIM_CHARGES_LIST_ENDPOINT}?page=${currentPage}`;
      const bodyData = {
        ...(cost_head_id && { cost_head_id }),
        ...(cost_type_id && { cost_type_id }),
      };

      try {
        const response = await getData(query, false, bodyData);
        if (response?.status === 200) {
          const data = response.data?.data?.data || [];
          // Add unique key if id is missing
          const dataWithKeys = data.map((item, index) => ({
            ...item,
            key: item.id || `charges-${index}`,
          }));

          setTableData(dataWithKeys);
          setMetaData({
            current: response.data?.data?.current_page || 1,
            defaultCurrent: response?.data?.data?.from || 1,
            defaultPageSize: response?.data?.data?.per_page || 10,
            total: response.data?.data?.total || 0,
          });
        } else {
          setFetchError("Could not load this list.");
        }
      } catch (error) {
        console.error("Charges fetch error:", error);
        setFetchError("Could not load this list.");
      } finally {
        setLoading(false);
      }
    },
    [currentPage],
  );

  // Effects
  useEffect(() => {
    getAllCharges(filterData);
  }, [refresh, filterData, getAllCharges]);

  const onModalOpen = () => {
    setIsModalOpen(true);
  };

  const onCloseModal = useCallback(() => {
    chargesForm.resetFields();
    setIsModalOpen(false);
    setIsEdit(false);
  }, [chargesForm]);

  const refetch = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);

  const deleteSelectedCharge = useCallback(
    async (id) => {
      setLoading(true);
      try {
        // Confirm before deleting
        Modal.confirm({
          title: "Confirm Deletion",
          content: "Are you sure you want to delete this Charge?",
          okText: "Yes, Delete",
          okType: "danger",
          cancelText: "Cancel",
          onOk: async () => {
            try {
              const response = await deleteData(
                `${EXIM_CHARGES_DELETE_ENDPOINT}/${id}`,
              );

              if (
                response &&
                (response?.code === 200 || response?.status === 200)
              ) {
                refetch();
              }
            } catch (error) {
              console.error("Delete charge error:", error);
            } finally {
              setLoading(false);
            }
          },
          onCancel: () => {
            setLoading(false);
          },
        });
      } catch (error) {
        console.error("Delete confirmation error:", error);
        setLoading(false);
      }
    },
    [refetch],
  );

  const handleChargesSubmit = useCallback(
    async (formValues) => {
      setLoading(true);
      const isEditMode = !!formValues.id;

      // Request body based on API requirements
      const bodyData = {
        cost_head_id: formValues?.cost_head_id,
        cost_type_id: formValues?.cost_type_id,
        amount: formValues?.amount,
      };

      try {
        let response;

        if (isEditMode) {
          // Edit operation - PUT request
          response = await putData(
            `${EXIM_CHARGES_EDIT_ENDPOINT}/${formValues.id}`,
            bodyData,
          );
        } else {
          // Create operation - POST request
          response = await postData(EXIM_CHARGES_ADD_ENDPOINT, bodyData);
        }

        if (response && (response?.code === 200 || response?.status === 200)) {
          onCloseModal();
          refetch();
        }
      } catch (error) {
        console.error(
          `${isEditMode ? "Edit" : "Create"} charges error:`,
          error,
        );
      } finally {
        setLoading(false);
      }
    },
    [onCloseModal, refetch],
  );

  const openModalForEdit = useCallback(
    (record) => {
      setIsEdit(true);
      chargesForm.setFieldsValue({
        id: record.id,
        cost_head_id: record.cost_head_id,
        cost_type_id: record.cost_type_id,
        amount: record.amount,
      });
      setIsModalOpen(true);
    },
    [chargesForm],
  );

  const changePage = (newPage) => {
    setCurrentPage(newPage);
  };

  const tableColumns = useMemo(
    () => [
      {
        title: "Cost Head",
        dataIndex: ["head", "name"],
        width: "20%",
        key: "cost_head_name",
        ellipsis: true,
      },
      {
        title: "Cost Type",
        dataIndex: ["type", "name"],
        width: "20%",
        key: "cost_type_name",
        ellipsis: true,
      },
      {
        title: "Amount",
        dataIndex: "amount",
        width: "15%",
        key: "amount",
        render: (amount) => amount?.toLocaleString() || "0",
        ellipsis: true,
      },

      {
        title: "Action",
        key: "action",
        width: "15%",
        render: (_, record) => (
          <div
            className="action-buttons"
            style={{ display: "flex", gap: "8px" }}
          >
            {hasPermission([NO_PERMISSION_REQUIRED]) && (
              <Button
                type="primary"
                size="small"
                onClick={() => openModalForEdit(record)}
                className="edit-button"
                style={{ backgroundColor: "#1890ff" }}
              >
                Edit
              </Button>
            )}
            {hasPermission([NO_PERMISSION_REQUIRED]) && (
              <Button
                type="primary"
                danger
                size="small"
                onClick={() => deleteSelectedCharge(record.id)}
                className="delete-button"
              >
                Delete
              </Button>
            )}
          </div>
        ),
      },
    ],
    [openModalForEdit, deleteSelectedCharge],
  );

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={getAllCharges}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
      />
    ),
    [showFilter, tableData, getAllCharges],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={onModalOpen}
                disabled={!hasPermission([NO_PERMISSION_REQUIRED])}
                addButtonText={
                  hasPermission([NO_PERMISSION_REQUIRED])
                    ? "Add New"
                    : "No Permission"
                }
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>Charges Configuration</Title>}
          />
        }
        content={
          <>
            {memoizedCustomFilter}

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => getAllCharges(filterData)}
                dataSource={tableData}
                columns={tableColumns}
                rowKey="id"
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize || 10,
                  current: metaData?.current || 1,
                  onChange: changePage,
                  defaultCurrent: metaData?.defaultCurrent || 1,
                  total: metaData?.total || 0,
                  showTotal: (total) => `Total ${total} items`,
                }}
                bordered
                size="middle"
                scroll={{
                  x: "max-content",
                  y: tableData.length > 10 ? 500 : undefined,
                }}
                rowClassName={(record, index) =>
                  index % 2 === 0 ? "table-row-light" : "table-row-dark"
                }
                locale={{
                  emptyText: loading
                    ? "Loading data..."
                    : "No exim accounts found",
                }}
                style={{ borderRadius: "8px", overflow: "hidden" }}
              />
            </div>
          </>
        }
      />
      <Modal
        open={isModalOpen}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {isEdit ? (
              <span style={{ color: "#1890ff" }}>Edit Charge</span>
            ) : (
              <span style={{ color: "#52c41a" }}>Create New Charge</span>
            )}
          </div>
        }
        onCancel={onCloseModal}
        maskStyle={{ backdropFilter: "blur(4px)" }}
        footer={null}
        destroyOnClose
        width={520}
        centered
        bodyStyle={{ padding: "24px" }}
      >
        <Form
          form={chargesForm}
          layout="vertical"
          onFinish={handleChargesSubmit}
          initialValues={{}}
          requiredMark="optional"
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="id" hidden />
              <Form.Item
                name="cost_head_id"
                label="Cost Head"
                rules={[
                  {
                    required: true,
                    message: "Cost Head is required",
                  },
                ]}
              >
                <Select
                  placeholder="Select cost head"
                  options={costHeadOptions}
                  showSearch
                  filterOption={(input, option) =>
                    option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ width: "100%", borderRadius: "4px" }}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="cost_type_id"
                label="Cost Type"
                rules={[
                  {
                    required: true,
                    message: "Cost Type is required",
                  },
                ]}
              >
                <Select
                  placeholder="Select cost type"
                  options={costTypeOptions}
                  showSearch
                  filterOption={(input, option) =>
                    option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ width: "100%", borderRadius: "4px" }}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="amount"
                label="Amount"
                rules={[
                  {
                    required: true,
                    message: "Amount is required",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Enter amount"
                  min={0}
                  step={1}
                  style={{ width: "100%", borderRadius: "4px" }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row
            gutter={16}
            className="form-actions"
            style={{ marginTop: "12px" }}
          >
            <Col span={12}>
              <Button
                type="default"
                danger
                style={{ width: "100%", borderRadius: "4px" }}
                onClick={onCloseModal}
              >
                Cancel
              </Button>
            </Col>
            <Col span={12}>
              <Button
                type="primary"
                htmlType="submit"
                style={{ width: "100%", borderRadius: "4px" }}
                loading={loading}
              >
                {isEdit ? "Update" : "Save"}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}
